import React, { useMemo } from "react";

import FilterListIcon from "@mui/icons-material/FilterList";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import Radio from "@mui/material/Radio";

import { GetFeedsQueryParamsV2 } from "@fartherfinance/frontend/api/Notifications/requests/getFeeds.v2";
import {
  FeedStatus,
  FeedV2,
} from "@fartherfinance/frontend/api/Notifications/Types";
import { FeedId } from "@fartherfinance/frontend/api/Types";

import { FEEDS_DEFAULT_QUERY_PARAMS } from "../common";
import Notification from "../Notification/Notification";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import CheckboxV2 from "@src/sharedComponents/Checkbox/CheckboxV2";
import IconButton from "@src/sharedComponents/IconButton/IconButton";
import InfiniteScroll from "@src/sharedComponents/InfiniteScroll/InfiniteScroll";
import Menu from "@src/sharedComponents/Menu/Menu";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./NotificationsList.module.css";

const SKELETON_COUNT = 25;
const SKELETON_COUNT_LOAD_MORE = 6;

type NotificationsListProps = {
  feeds: FeedV2[];
  isEmpty: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  selectedNotificationId: FeedId | undefined;
  queryParams: GetFeedsQueryParamsV2;
  onLoadMore: () => void;
  onMarkAllAsRead: () => void;
  onNotificationStatusToggle: (feed: FeedV2, toggledStatus: FeedStatus) => void;
  onNotificationClick: (feed: FeedV2) => void;
  onQueryParamChange: (newQueryParams: GetFeedsQueryParamsV2) => void;
};

const NotificationsList: React.FC<NotificationsListProps> = ({
  feeds,
  isEmpty,
  isLoading,
  isLoadingMore,
  hasMore,
  selectedNotificationId,
  queryParams,
  onLoadMore,
  onMarkAllAsRead,
  onNotificationStatusToggle,
  onNotificationClick,
  onQueryParamChange,
}) => {
  const handleScrollIntersection = (
    entries: IntersectionObserverEntry[]
  ): void => {
    const [entry] = entries;

    if (entry.isIntersecting && hasMore && !isLoadingMore) {
      onLoadMore();
    }
  };

  const params = useMemo(() => {
    const { sort, filterByStatus } = queryParams;

    return {
      isSortByNewest: sort === "-createdTs",
      isSortByOldest: sort === "+createdTs",
      isFilterByUnread:
        filterByStatus &&
        filterByStatus.length === 1 &&
        filterByStatus.includes(FeedStatus.enum.UNREAD),
    };
  }, [queryParams]);

  const handleToggleSortByNewest = (): void => {
    onQueryParamChange({
      sort: params.isSortByNewest ? undefined : "-createdTs",
    });
  };

  const handleToggleSortByOldest = (): void => {
    onQueryParamChange({
      sort: params.isSortByOldest ? undefined : "+createdTs",
    });
  };

  const handleToggleFilterByStatus = (): void => {
    onQueryParamChange({
      filterByStatus: params.isFilterByUnread
        ? FEEDS_DEFAULT_QUERY_PARAMS.filterByStatus
        : [FeedStatus.enum.UNREAD],
    });
  };

  const handleClearFilters = (): void => {
    onQueryParamChange({
      filterByStatus: FEEDS_DEFAULT_QUERY_PARAMS.filterByStatus,
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.toolbar}>
          <Text variant="title2">Your Notifications</Text>
          <Menu
            options={[
              {
                label: "Mark all as read",
                isCloseOnClick: true,
                onClick: onMarkAllAsRead,
              },
            ]}
          >
            {({ handleOpenMenu }) => (
              <IconButton
                size="small"
                onClick={handleOpenMenu}
                IconComponent={MoreHorizIcon}
              />
            )}
          </Menu>
        </div>

        <div className={styles.toolbar}>
          <Menu
            options={[
              {
                label: "View By:",
                isReadOnly: true,
                onClick: () => void null,
              },
              {
                label: "Newest",
                isCloseOnClick: true,
                onClick: handleToggleSortByNewest,
                IconComponent: (
                  <Radio
                    classes={{ root: styles.radio, checked: styles.checked }}
                    size="small"
                    checked={params.isSortByNewest}
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                  />
                ),
              },
              {
                label: "Oldest",
                isCloseOnClick: true,
                onClick: handleToggleSortByOldest,
                IconComponent: (
                  <Radio
                    classes={{ root: styles.radio, checked: styles.checked }}
                    size="small"
                    checked={params.isSortByOldest}
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                  />
                ),
              },
            ]}
          >
            {({ handleOpenMenu }) => (
              <IconButton
                size="small"
                onClick={handleOpenMenu}
                iconClassName={styles.icon}
                IconComponent={SwapVertIcon}
              />
            )}
          </Menu>

          {false && (
            <Menu
              options={[
                {
                  label: "Filter By:",
                  isReadOnly: true,
                  onClick: () => void null,
                },
                {
                  label: "Unread",
                  isCloseOnClick: true,
                  onClick: handleToggleFilterByStatus,
                  IconComponent: (
                    <CheckboxV2
                      className={styles.checkbox}
                      checked={params.isFilterByUnread}
                    />
                  ),
                },
              ]}
            >
              {({ handleOpenMenu }) => (
                <IconButton
                  size="small"
                  onClick={handleOpenMenu}
                  iconClassName={styles.icon}
                  IconComponent={FilterListIcon}
                />
              )}
            </Menu>
          )}
        </div>
      </div>

      <div className={styles.list}>
        {isLoading && (
          <>
            {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
              <Notification key={index} isLoading={true} />
            ))}
          </>
        )}

        {isEmpty && !isLoading && (
          <div className={styles.emptyContainer}>
            <div className={styles.emptyContent}>
              <Text variant="title1">Notification list is empty</Text>

              {params.isFilterByUnread && (
                <Button
                  variant="outlined"
                  buttonType="primary"
                  text="Clear Filters"
                  onClick={handleClearFilters}
                />
              )}
            </div>
          </div>
        )}

        {!isEmpty && !isLoading && (
          <InfiniteScroll
            className={styles.infiniteScrollContainer}
            onIntersection={handleScrollIntersection}
            intersectionThreshold={2}
          >
            {feeds.map((feed) => (
              <Notification
                key={feed.feedId}
                message={feed.data.body}
                topic={feed.data.subject}
                isUnseen={feed.status === FeedStatus.enum.UNREAD}
                isSelected={feed.feedId === selectedNotificationId}
                isLoading={false}
                createdAt={feed.createdTs}
                onStatusToggle={(newStatus) =>
                  onNotificationStatusToggle(feed, newStatus)
                }
                onClick={() => onNotificationClick(feed)}
              />
            ))}
            {isLoadingMore && (
              <>
                {Array.from({ length: SKELETON_COUNT_LOAD_MORE }).map(
                  (_, index) => (
                    <Notification key={index} isLoading={true} />
                  )
                )}
              </>
            )}
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};

export default NotificationsList;
